if (!customElements.get('wlsn-buybox-gallery')) {
  class WLSNBuyboxGallery extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        disabled: 'disabled',
        mainSlider: 'js-buybox-gallery-main-slider',
        thumbSlider: 'js-buybox-gallery-thumb-slider',
        mainSlide: 'js-buybox-gallery-main-slide',
        thumbSlide: 'js-buybox-gallery-thumb-slide',
        thumbContainer: 'js-buybox-gallery-thumb-container',
        mainNavButtonPrev: 'js-buybox-gallery-main-nav-button-prev',
        mainNavButtonNext: 'js-buybox-gallery-main-nav-button-next',
        thumbNavButtonPrev: 'js-buybox-gallery-thumb-nav-button-prev',
        thumbNavButtonNext: 'js-buybox-gallery-thumb-nav-button-next',
        pagination: 'js-buybox-gallery-pagination',
      };

      this.bindMethods = {};

      this.mainSwiper = null;
      this.thumbSwiper = null;
      this.minThumbsForSlider = 2;
      this.layout = parseInt(this.dataset.layout) || 1;
      this.isMobile = window.innerWidth < 1024;
      
      // Bind methods
      this.handleThumbHover = this.handleThumbHover.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        mainSlider: this.querySelector(`.${this.classes.mainSlider}`),
        thumbSlider: this.querySelector(`.${this.classes.thumbSlider}`),
        mainSlides: this.querySelectorAll(`.${this.classes.mainSlide}`),
        thumbSlides: this.querySelectorAll(`.${this.classes.thumbSlide}`),
        thumbContainer: this.querySelector(`.${this.classes.thumbContainer}`),
        mainNavButtonPrev: this.querySelector(`.${this.classes.mainNavButtonPrev}`),
        mainNavButtonNext: this.querySelector(`.${this.classes.mainNavButtonNext}`),
        thumbNavButtonPrev: this.querySelector(`.${this.classes.thumbNavButtonPrev}`),
        thumbNavButtonNext: this.querySelector(`.${this.classes.thumbNavButtonNext}`),
        pagination: this.querySelector(`.${this.classes.pagination}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.initThumbSlider();
      this.initMainSlider();
      
      if (this.layout === 2) {
        this.initLayout2();
        window.addEventListener('resize', this.handleResize);
      }
    }
    
    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.layout === 2) {
        window.removeEventListener('resize', this.handleResize);
        this.removeThumbHoverListeners();
      }
      
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
      }
      if (this.thumbSwiper) {
        this.thumbSwiper.destroy(true, true);
      }
    }

    /**
     * Handle resize event
     * @returns {void}
     */
    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 1024;
      
      if (wasMobile !== this.isMobile && this.layout === 2) {
        this.removeThumbHoverListeners();
        if (!this.isMobile) {
          this.addThumbHoverListeners();
        }
      }
    }
    
    /**
     * Initialize Layout 2 specific features
     * @returns {void}
     */
    initLayout2() {
      if (!this.isMobile) {
        this.addThumbHoverListeners();
        // Set initial active state
        this.updateThumbActiveState();
      }
    }
    
    /**
     * Add hover listeners to thumbnails for Layout 2
     * @returns {void}
     */
    addThumbHoverListeners() {
      const { thumbSlides } = this.elements;
      if (!thumbSlides) return;
      
      thumbSlides.forEach((thumbSlide) => {
        thumbSlide.addEventListener('mouseenter', this.handleThumbHover);
      });
    }
    
    /**
     * Remove hover listeners from thumbnails
     * @returns {void}
     */
    removeThumbHoverListeners() {
      const { thumbSlides } = this.elements;
      if (!thumbSlides) return;
      
      thumbSlides.forEach((thumbSlide) => {
        thumbSlide.removeEventListener('mouseenter', this.handleThumbHover);
      });
    }
    
    /**
     * Handle thumbnail hover for Layout 2
     * @param {Event} e - Mouse event
     * @returns {void}
     */
    handleThumbHover(e) {
      if (!this.mainSwiper || this.isMobile) return;
      
      const slideIndex = parseInt(e.currentTarget.dataset.slideIndex);
      if (!isNaN(slideIndex)) {
        // For loop mode, use slideToLoop to ensure correct slide is shown
        // slideToLoop uses the real index (0-based from original slides)
        if (this.mainSwiper.params.loop) {
          this.mainSwiper.slideToLoop(slideIndex, 0); // 0 = no speed, instant
        } else {
          this.mainSwiper.slideTo(slideIndex, 0);
        }
      }
    }

    /**
     * Initialize the thumbnail slider
     * @returns {void}
     */
    initThumbSlider() {
      const { thumbContainer, thumbSlider, thumbSlides } = this.elements;
      if (!thumbContainer || !thumbSlider) return;

      // Check if we have enough thumbnails to show slider
      if (thumbSlides.length < this.minThumbsForSlider) {
        this.hideThumbSlider();
        return;
      }

      const thumbConfig = {
        loop: false,
        spaceBetween: 8,
        slidesPerView: 4,
        watchSlidesProgress: true,
        direction: 'vertical',
        clickable: true,
      };

      // Layout 2: Add navigation buttons for thumb slider
      if (this.layout === 2) {
        thumbConfig.navigation = {
          nextEl: this.elements.thumbNavButtonNext,
          prevEl: this.elements.thumbNavButtonPrev,
        };
      }

      this.thumbSwiper = new Swiper(thumbSlider, thumbConfig);
      
      // Check if we have insufficient slides for alignment
      // Add class if slides don't fill the viewport (less than slidesPerView) or if Swiper is locked
      // Applies to both Layout 1 and Layout 2
      if (thumbSlides.length < thumbConfig.slidesPerView || this.thumbSwiper.isLocked) {
        thumbSlider.classList.add('wlsn-buybox-gallery__thumb-slider--insufficient-slides');
      }
    }

    /**
     * Initialize the main slider
     * @returns {void}
     */
    initMainSlider() {
      const { mainSlider } = this.elements;
      if (!mainSlider) return;

      const mainConfig = {
        loop: true,
        spaceBetween: 10,
        keyboard: {
          enabled: true,
        },
        grabCursor: true,
      };

      // Layout 1: Use thumbs and main navigation buttons
      if (this.layout === 1) {
        mainConfig.thumbs = {
          swiper: this.thumbSwiper,
        };
        mainConfig.navigation = {
          nextEl: this.elements.mainNavButtonNext,
          prevEl: this.elements.mainNavButtonPrev,
        };
      }

      // Layout 2: Use pagination for mobile, no thumbs connection
      if (this.layout === 2) {
        if (this.isMobile && this.elements.pagination) {
          mainConfig.pagination = {
            el: this.elements.pagination,
            clickable: true,
            type: 'bullets',
          };
        }
        // For desktop, sync active state but don't let thumbs control main slider
        if (!this.isMobile) {
          mainConfig.on = {
            slideChange: () => {
              this.updateThumbActiveState();
            },
            init: () => {
              this.updateThumbActiveState();
            },
          };
        }
      }

      this.mainSwiper = new Swiper(mainSlider, mainConfig);
    }
    
    /**
     * Update thumb active state based on main slider position
     * @returns {void}
     */
    updateThumbActiveState() {
      if (!this.mainSwiper || !this.thumbSwiper || this.layout !== 2 || this.isMobile) return;
      
      const { thumbSlides } = this.elements;
      if (!thumbSlides) return;
      
      const activeIndex = this.mainSwiper.realIndex;
      
      thumbSlides.forEach((thumbSlide, index) => {
        if (index === activeIndex) {
          thumbSlide.classList.add(this.classes.active);
          thumbSlide.querySelector('.wlsn-buybox-gallery__thumb-wrapper')?.classList.add('swiper-slide-thumb-active');
        } else {
          thumbSlide.classList.remove(this.classes.active);
          thumbSlide.querySelector('.wlsn-buybox-gallery__thumb-wrapper')?.classList.remove('swiper-slide-thumb-active');
        }
      });
    }

    /**
     * Hide the thumbnail slider
     * @returns {void}
     */
    hideThumbSlider() {
      const { thumbContainer, thumbSlider } = this.elements;
      if (thumbContainer) {
        thumbContainer.classList.add(this.classes.hidden);
      }
      // Add class to indicate insufficient slides for Layout 2 alignment
      if (thumbSlider && this.layout === 2) {
        thumbSlider.classList.add('wlsn-buybox-gallery__thumb-slider--insufficient-slides');
      }
    }
  }
  customElements.define('wlsn-buybox-gallery', WLSNBuyboxGallery);
}
